<template>
  <VeeForm v-slot="{ errors, handleSubmit, meta }" class="w-80">
    <VeeField
      v-slot="{ field, errorMessage }"
      v-model="formModel.quantity"
      name="quantity"
    >
      <NText
        variant="caption1"
        weight="medium"
        class="text-neutral-secondary-press block pr-3 mb-2"
      >
        {{ t('misc.quantity') }}
      </NText>

      <NInput
        v-mask="{
          alias: 'positiveNumber',
        }"
        :model-value="formModel.quantity"
        v-bind="field"
        class="my-2"
        type="tel"
        :error="errorMessage || errors.quantity"
        show-error
        input-class="text-right"
        :min="0"
        @focusin="$event.target.select()"
      />
    </VeeField>

    <VeeField
      v-slot="{ field, errorMessage }"
      v-model="formModel.price"
      name="price"
    >
      <NText
        variant="caption1"
        weight="medium"
        class="text-neutral-secondary-press block pr-3 mb-2"
      >
        {{ t('modules.portfolio.net_average') }}
      </NText>

      <NInput
        v-mask="{
          alias: 'positiveNumber',
        }"
        :model-value="formModel.price"
        v-bind="field"
        class="my-2"
        type="tel"
        input-class="text-right"
        :error="errorMessage || errors.price"
        show-error
        :min="0"
        @focusin="$event.target.select()"
      />
    </VeeField>

    <div class="flex mt-4">
      <NButton
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'editVirtualPortfolioDialog',
          component: 'submitButton',
        }"
        :disabled="isLoadingUpdateVirtualPortfolioItem || !meta.valid"
        class="me-3 w-1/2"
        @click="handleSubmit($event, submit)"
      >
        <NText weight="bold" variant="caption1">
          {{ t('misc.submit') }}
        </NText>
      </NButton>

      <NButton
        variant="plain"
        color="gray"
        class="w-1/2"
        @click.prevent="remove()"
      >
        <NText weight="bold" variant="caption1">
          {{ t('common.cancel') }}
        </NText>
      </NButton>
    </div>
  </VeeForm>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { reactive } from 'vue';
import { VirtualPortfolioItem } from '@libs/api-reference';
import { useDialog, useToast } from '@negar/core';
import { useQueryClient } from '@libs/use';
import {
  PORTFOLIO_QUERY_KEYS,
  useUpdateVirtualPortfolioItemQuery,
} from '@core/portfolio';

import vMask from '@/directives/mask';

type EditNetAveragePriceProps = {
  selectedSymbol: VirtualPortfolioItem;
};

type FormModel = {
  quantity: number;
  price: number;
};

const props = defineProps<EditNetAveragePriceProps>();

const { t } = useI18n();

const { remove } = useDialog();
const toast = useToast();

const queryClient = useQueryClient();

const formModel = reactive<FormModel>({
  price: Math.round(props.selectedSymbol.averagePrice),
  quantity: Math.round(props.selectedSymbol.quantity),
});

const { updateVirtualPortfolioItem, isLoadingUpdateVirtualPortfolioItem } =
  useUpdateVirtualPortfolioItemQuery({
    onSuccess() {
      toast.neutral({
        message: t('modules.portfolio.virtualPortfolio.editSuccessMessage', {
          name: props.selectedSymbol.instrumentName,
          ...formModel,
        }),
        duration: 3000,
      });

      queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.virtualPortfolio);

      remove();
    },
    onReject() {
      toast.negative({
        message: t('modules.portfolio.virtualPortfolio.editFailedMessage', {
          name: props.selectedSymbol.instrumentName,
        }),
        duration: 3000,
      });
    },
  });

const submit = (formModel: FormModel) => {
  updateVirtualPortfolioItem({
    price: Math.round(formModel.price),
    virtualAssetId: props.selectedSymbol.id,
    quantity: formModel.quantity,
  });
};
</script>
